// محرّك التقارير — يعمل على النسخة المحلية، فيعمل بلا إنترنت وبلا انتظار.
//
// لماذا لا يُحسب على الخادم؟ بيانات أسرة واحدة بضعة آلاف حركة على الأكثر،
// وحسابها في المتصفح فوري ويعمل بلا اتصال. نقلها إلى الخادم يكلّف رحلة شبكة
// لكل تغيير فترة، ويعطّل التقارير كلياً بلا إنترنت — مقابل لا شيء عند هذا الحجم.
// حين تتجاوز محفظة واحدة ~٥٠ ألف حركة، انقل التجميع إلى SQL.
//
// قاعدة أساسية: المقارنة تقارن أياماً متساوية دائماً. مقارنة ٢٠ يوماً مضت
// بشهر كامل تُظهر انخفاضاً وهمياً في كل بند — وهو تقرير يطمئن كذباً.

import { state, toBase, destName, typeName, typeById, liveEntries } from './store.js';
import { DAY_MS, MONTHS, startOfDay, endOfDay, monthStart, daysInMonth, shortDate } from './util.js';

export const PERIODS = [
  ['today',  'اليوم'],
  ['week',   'آخر ٧ أيام'],
  ['month',  'هذا الشهر'],
  ['prev',   'الشهر السابق'],
  ['q',      '٣ أشهر'],
  ['year',   'السنة'],
  ['custom', 'من — إلى']
];

const at = e => new Date(e.occurredAt).getTime();
const isOut = e => e.kind !== 'in';

export function range(period, customFrom, customTo, now = Date.now()){
  const today = startOfDay(now);
  const eod = endOfDay(now);
  let r;

  switch (period){
    case 'today':
      r = { f: today, t: eod, label: 'اليوم',
            pf: today - DAY_MS, pt: today - 1, pLabel: 'أمس' };
      break;

    case 'week':
      r = { f: today - 6 * DAY_MS, t: eod, label: 'آخر ٧ أيام',
            pf: today - 13 * DAY_MS, pt: today - 7 * DAY_MS - 1, pLabel: 'الأسبوع السابق' };
      break;

    case 'month': {
      const f = monthStart(now, 0);
      const elapsed = Math.round((today - f) / DAY_MS) + 1;
      const pf = monthStart(now, 1);
      r = { f, t: eod, label: MONTHS[new Date(now).getMonth()],
            pf, pt: pf + elapsed * DAY_MS - 1,
            pLabel: `أول ${elapsed} يوماً من ${MONTHS[new Date(pf).getMonth()]}` };
      break;
    }

    case 'prev': {
      const f = monthStart(now, 1);
      const pf = monthStart(now, 2);
      r = { f, t: monthStart(now, 0) - 1, label: MONTHS[new Date(f).getMonth()],
            pf, pt: f - 1, pLabel: MONTHS[new Date(pf).getMonth()] };
      break;
    }

    case 'q':
      r = { f: today - 89 * DAY_MS, t: eod, label: 'آخر ٣ أشهر',
            pf: today - 179 * DAY_MS, pt: today - 90 * DAY_MS - 1, pLabel: 'الـ٣ أشهر السابقة' };
      break;

    case 'year': {
      const y = new Date(now).getFullYear();
      const f = new Date(y, 0, 1).getTime();
      r = { f, t: eod, label: `سنة ${y}`,
            pf: new Date(y - 1, 0, 1).getTime(), pt: f - 1, pLabel: 'السنة السابقة' };
      break;
    }

    default: {
      const f = startOfDay(customFrom || monthStart(now, 0));
      const t = endOfDay(customTo || today);
      const span = Math.max(DAY_MS, t - f + 1);
      r = { f, t, label: `${shortDate(f)} — ${shortDate(t)}`,
            pf: f - span, pt: f - 1, pLabel: 'الفترة السابقة المماثلة' };
    }
  }

  r.days = Math.max(1, Math.round((Math.min(r.t, eod) - r.f) / DAY_MS) + 1);
  return r;
}

const inRange = (e, f, t) => { const x = at(e); return x >= f && x <= t; };

export function spend(f, t, pred){
  let s = 0;
  for (const e of liveEntries()){
    if (!isOut(e) || !inRange(e, f, t)) continue;
    if (pred && !pred(e)) continue;
    s += toBase(e);
  }
  return s;
}

export function income(f, t){
  let s = 0;
  for (const e of liveEntries()){
    if (isOut(e) || !inRange(e, f, t)) continue;
    s += toBase(e);
  }
  return s;
}

export function count(f, t, pred){
  let n = 0;
  for (const e of liveEntries()){
    if (inRange(e, f, t) && (!pred || pred(e))) n++;
  }
  return n;
}

export function entriesIn(f, t, pred){
  return liveEntries()
    .filter(e => inRange(e, f, t) && (!pred || pred(e)))
    .sort((a, b) => at(b) - at(a));
}

/** تجميع المصروف حسب أحد البُعدين. */
export function groupBy(f, t, key){
  const tally = new Map();
  let untyped = 0;
  for (const e of liveEntries()){
    if (!isOut(e) || !inRange(e, f, t)) continue;
    const id = key === 'dest' ? e.destId : e.typeId;
    const v = toBase(e);
    if (key === 'type' && !id) untyped += v;
    const k = id || '__none__';
    tally.set(k, (tally.get(k) || 0) + v);
  }
  const rows = [...tally.entries()].map(([id, value]) => ({
    id,
    name: id === '__none__' ? 'بلا نوع' : (key === 'dest' ? destName(id) : (typeName(id) || 'بلا نوع')),
    value,
    none: id === '__none__'
  })).sort((a, b) => b.value - a.value);
  return { rows, untyped };
}

/** ما ارتفع وما انخفض مقارنة بالفترة السابقة المكافئة. */
export function movers(f, t, pf, pt, key, minDelta = 20){
  const now = new Map(), was = new Map();
  for (const e of liveEntries()){
    if (!isOut(e)) continue;
    const id = (key === 'dest' ? e.destId : e.typeId) || '__none__';
    if (inRange(e, f, t)) now.set(id, (now.get(id) || 0) + toBase(e));
    else if (inRange(e, pf, pt)) was.set(id, (was.get(id) || 0) + toBase(e));
  }
  const list = [];
  for (const id of new Set([...now.keys(), ...was.keys()])){
    if (id === '__none__') continue;
    const n = now.get(id) || 0, w = was.get(id) || 0;
    if (n < minDelta && w < minDelta) continue;
    const name = key === 'dest' ? destName(id) : typeName(id);
    if (!name) continue;
    list.push({ id, name, now: n, was: w, delta: n - w });
  }
  list.sort((a, b) => b.delta - a.delta);
  return {
    up: list.filter(m => m.delta > minDelta).slice(0, 4),
    down: list.filter(m => m.delta < -minDelta).slice(-3).reverse()
  };
}

/** أعمدة الاتجاه — تتكيّف تلقائياً: يومي، أسبوعي، ثم شهري. */
export function buckets(f, t, now = Date.now()){
  const end = Math.min(t, endOfDay(now));
  const days = Math.round((end - f) / DAY_MS) + 1;
  const size = days <= 45 ? 1 : (days <= 220 ? 7 : 30);
  const list = [];
  let max = 0;
  for (let s = f; s <= end; s += size * DAY_MS){
    const e2 = Math.min(s + size * DAY_MS - 1, end);
    const value = spend(s, e2);
    list.push({ from: s, to: e2, value });
    if (value > max) max = value;
  }
  return { list, max, size, unit: size === 1 ? 'يومياً' : size === 7 ? 'أسبوعياً' : 'شهرياً' };
}

/** الثابت مقابل المتغيّر — الرقم الوحيد الذي يمكن للمستخدم التحكم به. */
export function fixedVsVariable(f, t){
  let fixed = 0, variable = 0;
  for (const e of liveEntries()){
    if (!isOut(e) || !inRange(e, f, t)) continue;
    const ty = e.typeId ? typeById(e.typeId) : null;
    if (ty && ty.isFixed) fixed += toBase(e);
    else variable += toBase(e);
  }
  return { fixed, variable, total: fixed + variable };
}

export function biggest(f, t, limit = 6){
  return liveEntries()
    .filter(e => isOut(e) && inRange(e, f, t))
    .sort((a, b) => toBase(b) - toBase(a))
    .slice(0, limit);
}

/** مصفوفة التقاطع: نوع × وجهة. لا يمكن لأي تصنيف شجري أن ينتجها. */
export function crossTable(f, t, maxRows = 7){
  const typeRows = groupBy(f, t, 'type').rows.filter(r => !r.none).slice(0, maxRows);
  const destRows = groupBy(f, t, 'dest').rows.filter(r => !r.none).slice(0, 6);
  const cells = new Map();
  let max = 0;
  for (const e of liveEntries()){
    if (!isOut(e) || !inRange(e, f, t) || !e.typeId) continue;
    const k = `${e.typeId}|${e.destId}`;
    const v = (cells.get(k) || 0) + toBase(e);
    cells.set(k, v);
    if (v > max) max = v;
  }
  return { typeRows, destRows, cells, max };
}

/** توقّع نهاية الشهر بالمعدّل الحالي. */
export function projection(f, days, now = Date.now()){
  const total = spend(f, endOfDay(now));
  return Math.round((total / Math.max(1, days)) * daysInMonth(now));
}
